import React from "react";
import { Check, Palette } from "lucide-react";
import { usePrimaryColor, BRAND_COLORS, COLOR_LABELS } from "./PrimaryColorContext";

/**
 * PrimaryColorPicker - Visual color palette selector for the app's primary color.
 *
 * Displays a 4-column grid of color swatches representing the 16 available
 * Tailwind brand colors. Each swatch uses native Tailwind bg-* classes.
 * Selected color is highlighted with a checkmark and scale effect.
 *
 * @author Forge + opencode (glm-5.1)
 * @version 2.0
 * @since 2026-04-17
 */
export default function PrimaryColorPicker() {
  const { primaryColor, setPrimaryColor } = usePrimaryColor();

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="w-5 h-5 text-slate-600" />
        <span className="text-sm font-medium text-slate-700">
          Choose the primary brand color for the application.
        </span>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {BRAND_COLORS.map((colorName) => {
          const isSelected = primaryColor === colorName;
          const label = COLOR_LABELS[colorName];

          return (
            <button
              key={colorName}
              onClick={() => setPrimaryColor(colorName)}
              className={`
                group relative flex flex-col items-center gap-1.5 p-3 rounded-xl
                border-2 transition-all duration-200 cursor-pointer
                hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1
                focus:ring-${colorName}-400
                ${isSelected
                  ? "border-slate-800 shadow-md bg-white"
                  : "border-slate-200 bg-white hover:border-slate-300"
                }
              `}
              aria-label={`Select ${label} as primary color`}
              aria-pressed={isSelected}
            >
              {/* Color swatch circle */}
              <div
                className={`
                  w-10 h-10 rounded-full relative transition-transform duration-200
                  bg-gradient-to-br from-${colorName}-600 to-${colorName}-800
                  shadow-${colorName}-200
                  ${isSelected ? "scale-110" : "group-hover:scale-105"}
                `}
              >
                {/* Checkmark for selected */}
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white drop-shadow-sm" strokeWidth={3} />
                  </div>
                )}
              </div>

              {/* Label */}
              <span
                className={`
                  text-xs font-medium transition-colors duration-200
                  ${isSelected ? "text-slate-900" : "text-slate-500 group-hover:text-slate-700"}
                `}
              >
                {label}
              </span>

              {/* Selected indicator dot */}
              {isSelected && (
                <div
                  className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white bg-${colorName}-600`}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Preview bar showing the selected color in context */}
      <div className="mt-5 p-3 rounded-lg border border-slate-200 bg-slate-50">
        <p className="text-xs text-slate-500 mb-2 font-medium">Preview</p>
        <div className="flex items-center gap-2">
          <div
            className={`h-8 w-24 rounded-md bg-gradient-to-br from-${primaryColor}-800 to-${primaryColor}-700`}
          />
          <div className={`h-8 w-24 rounded-md bg-${primaryColor}-600`} />
          <div className={`h-8 w-24 rounded-md bg-${primaryColor}-100`} />
          <div className={`h-8 w-24 rounded-md border bg-${primaryColor}-50 border-${primaryColor}-200`} />
          <div className={`h-8 w-24 rounded-md flex items-center justify-center text-xs font-medium text-white bg-${primaryColor}-700`}>
            Button
          </div>
        </div>
      </div>
    </div>
  );
}
